// VersionHistory.js
// ─────────────────────────────────────────────────────────────────────────────
// RECORD VERSION HISTORY VIEWER  (ISO 9001 §7.5.3)
//
// A drop-in modal that shows the change history of one record (certificate /
// instruction / variation). Versions are captured server-side by the
// version-snapshot Cloud Function triggers into a tamper-evident, append-only
// `versions` subcollection — this component only reads them.
//
// Usage:
//   const [hist, setHist] = useState(null);
//   ...<button onClick={() => setHist(row)}>History</button>
//   {hist && (
//     <VersionHistory
//       projectId={projectId} collection="instructions"
//       docId={hist.id} label={hist.siNumber}
//       onClose={() => setHist(null)}
//     />
//   )}
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { tokens } from "./theme";

const C = tokens.color;
const MONO = tokens.font?.mono || '"JetBrains Mono", monospace';

const CHANGE_META = {
  create: { bg: C.successSoft, text: C.success, border: C.successBorder, label: "Created" },
  update: { bg: C.infoSoft,    text: C.info,    border: C.infoBorder,    label: "Edited" },
  delete: { bg: C.dangerSoft,  text: C.danger,  border: C.dangerBorder,  label: "Deleted" },
};

function fmtTs(ts) {
  // Firestore Timestamp → readable; also tolerates a raw ISO string.
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : (ts ? new Date(ts) : null));
    if (!d || isNaN(d)) return "—";
    return d.toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

// Render a stored value compactly (handles Timestamps, objects, arrays).
function showVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (v?.toDate) return fmtTs(v);
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

const s = {
  overlay: { position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal: { background: C.surface, borderRadius: 14, width: "min(820px, 100%)", maxHeight: "88vh", overflowY: "auto", padding: 22, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", fontFamily: tokens.font?.body },
  title: { margin: 0, fontSize: 18, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", background: m.bg, color: m.text, border: `1px solid ${m.border}` }),
  entry: { border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10, background: C.surfaceAlt },
  th: { padding: "6px 8px", textAlign: "left", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `1px solid ${C.border}` },
  td: { padding: "6px 8px", fontSize: 12, borderBottom: `1px solid ${C.border}`, verticalAlign: "top", fontFamily: MONO },
  mono: { fontFamily: MONO },
};

function VersionEntry({ v }) {
  const [open, setOpen] = useState(false);
  const meta = CHANGE_META[v.changeType] || CHANGE_META.update;
  const fields = Array.isArray(v.changedFields) ? v.changedFields : [];
  return (
    <div style={s.entry}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={s.chip(meta)}>{meta.label}</span>
          <span style={{ ...s.mono, fontSize: 12.5, color: C.ink }}>{fmtTs(v.at)}</span>
          {v.changedBy ? <span style={{ fontSize: 12, color: C.inkMuted }}>by {v.changedBy}</span> : null}
          {v.changedVia && v.changedVia !== "manual" ? <span style={{ fontSize: 11, color: C.inkSubtle }}>({v.changedVia})</span> : null}
        </div>
        {v.changeType === "update" && fields.length > 0 && (
          <button style={s.btn} onClick={() => setOpen((o) => !o)}>{open ? "Hide" : `${fields.length} field${fields.length !== 1 ? "s" : ""} changed`}</button>
        )}
      </div>

      {v.changeType === "update" && open && (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Field", "From", "To"].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {fields.map((f) => (
                <tr key={f}>
                  <td style={{ ...s.td, fontWeight: 700, color: C.ink }}>{f}</td>
                  <td style={{ ...s.td, color: C.danger }}>{showVal((v.before || {})[f])}</td>
                  <td style={{ ...s.td, color: C.success }}>{showVal((v.after || {})[f])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {v.changeType !== "update" && fields.length === 0 && (
        <p style={{ ...s.sub, marginTop: 6 }}>
          {v.changeType === "create" ? "Record created." : "Record deleted."}
        </p>
      )}
    </div>
  );
}

export default function VersionHistory({ projectId, collection: coll, docId, label, onClose }) {
  const [versions, setVersions] = useState(null); // null = loading

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!projectId || !coll || !docId) { setVersions([]); return undefined; }
    const q = query(
      collection(db, "projects", projectId, coll, docId, "versions"),
      orderBy("at", "desc")
    );
    const unsub = onSnapshot(q,
      (snap) => setVersions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.error("versions listener:", err); setVersions([]); }
    );
    return unsub;
  }, [projectId, coll, docId]);

  return createPortal(
    <div style={s.overlay}>
      <div style={s.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
          <div>
            <h3 style={s.title}>Version history — {label || docId}</h3>
            <p style={s.sub}>Every change is captured server-side and cannot be edited or deleted (ISO 9001 §7.5.3).</p>
          </div>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>

        {versions === null && <p style={s.sub}>Loading…</p>}
        {versions !== null && versions.length === 0 && (
          <p style={s.sub}>No history captured yet. Versions are recorded from the next change onward.</p>
        )}
        {versions !== null && versions.map((v) => <VersionEntry key={v.id} v={v} />)}
      </div>
    </div>,
    document.body
  );
}
